import {Observable} from "rxjs/internal/Observable"
import {Subject} from "rxjs/internal/Subject"
import {Subscription} from "rxjs/internal/Subscription"
import {PartialObserver} from "rxjs/internal/types"
import {IEBMessage} from "./managerConnector"
import {peers} from "./protos/eb.peers"
import {manager} from "./protos/eb.manager"
import {AsyncSubject} from "rxjs"

interface IEBPeerConnector extends IEBMessage<peers.Message> {
  sendOffer(): Observable<[string, string[]]>
  receiveAnswer(desc: string, iceCandidates: string[]): Observable<boolean>
  sendAnswer(desc: string, iceCandidates: string[]): Observable<[string, string[]]>
  next(msg: peers.Message): Observable<IPeerErrors>
  notifyStateChanges(observer?: PartialObserver<RTCIceConnectionState>): Subscription
  getState(): RTCIceConnectionState
  ID(): string
  close(): void
}

interface IPeerErrors {
  Type: PeerErrorsType
  Args?: any
}

enum PeerErrorsType {
  NoError,
  SendError,
}
enum IceCandidateType {
  CandidateTypeHost = 0,
  CandidateTypeServerReflexive = 1,
  CandidateTypePeerReflexive = 2,
  CandidateTypeRelay = 3,
}

const createPeerConnector = (id?: string): IEBPeerConnector => {
  return new PeerConnector(id ? id : null)
}

const gatherIceCandidates = (): Observable<manager.IceCandidate[]> => {
  return new Observable<manager.IceCandidate[]>((subscriber) => {
    const config: RTCConfiguration = {
      iceServers: [{
        urls: ["stun:stun.easybroadcast.fr:3478"],
      }],
      iceTransportPolicy: "all",
    }
    const offerOptions: RTCOfferOptions = {
      offerToReceiveAudio: true,
    }
    let conn: RTCPeerConnection = new RTCPeerConnection(config)
    const candidates: RTCIceCandidate[] = new Array<RTCIceCandidate> ()
    let iceGatherTimer: ReturnType<typeof setTimeout> = null

    conn.onicecandidate = (ev: RTCPeerConnectionIceEvent): void => {
      if ((typeof ev !== "object") || (ev.candidate === undefined)) {
        return
      }
      if (ev.candidate === null) {
        subscriber.next(
          candidates.filter((ice: RTCIceCandidate): boolean => ice !== null)
            .map( (ice) => fromRTCIceToManagerIce(ice))
            .filter( (value, index, self) =>
              self.findIndex((ele) => ele && ele.Type === value.Type && ele.Address === value.Address) === index),
        )
        subscriber.complete()
        if (iceGatherTimer) {
          clearTimeout(iceGatherTimer)
          iceGatherTimer = null
        }
      }
      candidates.push(ev.candidate)
    }
    conn.onicegatheringstatechange = (ev: Event): void => {
      if (conn == null) {
        return
      }
      if (conn.iceGatheringState === "complete") {
        conn.close()
        conn = null
      }
    }

    setTimeout(
      () => {
        if (conn != null) {
          conn.close()
          conn = null
        }
        if (candidates.length) {
          subscriber.next(
            candidates.filter((ice: RTCIceCandidate): boolean => ice !== null)
              .map((ice) => fromRTCIceToManagerIce(ice))
              .filter( (value, index, self) =>
                  self.findIndex((ele) => ele && ele.Type === value.Type && ele.Address === value.Address) === index),
          )
          subscriber.complete()
        }
        if (iceGatherTimer) {
          clearTimeout(iceGatherTimer)
          iceGatherTimer = null
        }
        if (candidates.length) {
          return
        }
        subscriber.error(new Error("Timeout while gathering ICE candidates"))
      },
      3000,
    )
    conn.createDataChannel("")
    conn.createOffer(offerOptions)
        .then((res: RTCSessionDescriptionInit) => conn.setLocalDescription(res))
        .catch((err: Error) => {
          // console.log("Could not gather ICE candidates", err)
          subscriber.error(err)
        })
  })
}


const fromRTCIceToManagerIce = (candidate: RTCIceCandidate): manager.IceCandidate => {
  const pos: number = candidate.candidate.indexOf("candidate:") + 10
  const [...candidatesArr]: string[] = candidate.candidate.trim().substr(pos).split(/\s+/)
  const address = candidatesArr[4]
  const type = candidatesArr[7]
  if (address === "") {
    return null
  }

  let candidateType: number

  switch (type) {
    case "host":
      candidateType = IceCandidateType.CandidateTypeHost
      break
    case "srflx":
      candidateType = IceCandidateType.CandidateTypeServerReflexive
      break
    case "prflx":
      candidateType = IceCandidateType.CandidateTypePeerReflexive
      break
    case "relay":
      candidateType = IceCandidateType.CandidateTypeRelay
      break
    default:
      return null
  }
  return new manager.IceCandidate({ Type: candidateType, Address: address})
}

/*
PeerConnector class is used to connect to another peer
The id represents the id of another peer
 */
class PeerConnector implements IEBPeerConnector {
  // id is the peer id
  public id: string
  // peerChannelSubject is used for receiving message from a peer
  public peerChannelSubject: Subject<peers.Message>
  // peerStateChangesSubject is used for receiving signal of peer connection state
  private peerStateChangesSubject: Subject<RTCIceConnectionState>
  // rootSubscription is the inherent handler for peer messages
  // id is the peer id
  private peerConnection: RTCPeerConnection
  private pcCurrentState: RTCIceConnectionState
  private syncChannel: RTCDataChannel

  // Configurations
  private iceGatherTimeout: number = 2000

  private readonly iceCandidateArray: RTCIceCandidate[]
  private iceGatherSubject: Subject<string[]>
  private iceGatherTimer: ReturnType<typeof setTimeout>

  public constructor(id?: string) {
    this.id = id || null
    const config: RTCConfiguration = {
      // TODO: create a interface config stun server
      iceServers: [{
        urls: ["stun:stun.l.google.com:19302"],
      }],
      iceTransportPolicy: "all",
    }

    this.peerConnection = new RTCPeerConnection(config)

    this.peerConnection.addEventListener("icecandidate",
        (e: RTCPeerConnectionIceEvent): void => this.onIceCandidateHandle(e))
    this.peerConnection.addEventListener("iceconnectionstatechange",
        (e: Event): void => this.onIceConnectionStateChangeHandle(e))
    this.peerConnection.addEventListener("icegatheringstatechange",
        (e: Event): void => this.onIceGatheringStateChangeHandle(e))
    this.peerConnection.addEventListener("datachannel",
        (e: RTCDataChannelEvent): void => this.onDataChannel(e))
    this.pcCurrentState = "new"
    this.peerChannelSubject = new Subject()
    this.iceGatherSubject =  new AsyncSubject()
    this.peerStateChangesSubject = new Subject<RTCIceConnectionState>()
    this.iceCandidateArray = new Array<RTCIceCandidate>()
  }

  public ID = (): string => {
    return this.id
  }

  public close(): void {
    try {
      // if (this.iceGatherSubject) {
      //   this.iceGatherSubject = null
      // }
      // if (this.peerChannelSubject) {
      //   // this.peerChannelSubject.complete()
      //   this.peerChannelSubject = null
      // }
      // if (this.peerStateChangesSubject) {
      //   // this.peerStateChangesSubject.complete()
      //   this.peerStateChangesSubject = null
      // }
      if (this.syncChannel) {
        this.syncChannel.close()
        this.syncChannel = null
      }
      if (this.peerConnection) {
        this.peerConnection.close()
        this.peerConnection = null
      }
    } catch (e) {
      // Channel may be already closed or not yet opened.
    }
  }

  public sendOffer(): Observable<[string, string[]]> {
    const sourceCreateOffer = new Observable<[string, string[]]>((subscriber) => {
      this.peerConnection.createOffer()
          .then( (offer: RTCSessionDescriptionInit) => {
                // console.log(`peer ${this.id} create offer success`)
                this.peerConnection.setLocalDescription(offer).then(() => {
                  // console.log(`peer ${this.id} set local offer success`)
                  this.iceGatherSubject.subscribe( {
                    next: () => {},
                    complete: () => {
                      // console.log(`peer ${this.id} finish collect local offer and ice candidates`)
                      subscriber.next([this.parseSDP(this.peerConnection.localDescription),
                        this.parseIceCandidate(this.iceCandidateArray)])
                    },
                    error: () => {},
                  })
                }).catch((e) => {
                  // console.log(`peer ${this.id} set local description fail, error is: ${e.message}`)
                  subscriber.error(e)
                })
              },
          ).catch((e) => {
            // console.log(`peer ${this.id} create offer fail, error is: ${e.message}`)
            subscriber.error(e)
          })
    })
    this.syncChannel = this.peerConnection.createDataChannel("sync")
    this.bindDataChannelHandler(this.syncChannel)
    return sourceCreateOffer
  }

  public sendAnswer(desc: string, iceCandidates: string[]): Observable<[string, string[]]> {
    const sourceCreateAnswer = new Observable<[string, string[]]>( (subscriber) => {
      this.peerConnection.setRemoteDescription(this.toSessionSDP(desc, "offer"))
          .then(() => {
            // console.log(`peer ${this.id} set remote offer description success`)
            return this.peerConnection.createAnswer()
          }).then((answer: RTCSessionDescriptionInit) => {
        // console.log(`peer ${this.id} create local answer description success`)
        this.peerConnection.setLocalDescription(answer).then(() => {
          // console.log(`peer ${this.id} set local answer description success`)
          this.toIceCandidate(iceCandidates).forEach((ice, index) =>
              this.peerConnection.addIceCandidate(ice)
                  // .then(() => console.log(`peer ${this.id} add ice success ${ice.candidate}`))
                  .catch((e) => {
                    // console.warn(`peer ${this.id} add remote ice fail, error is ${e.message}`)
                    // console.warn(`peer ${this.id} add remote ice fail, error is ${e.name}`)
                    // console.warn(`${index}: peer signaling state is ${this.peerConnection.signalingState}`)
                    // console.warn(`${index}: peer connection state is ${this.peerConnection.iceConnectionState}`)
                    // console.warn(`${index}: ice value is: ${ice.candidate} ${ice.sdpMLineIndex} ${ice.sdpMid}`)
                  }),
          )
          this.iceGatherSubject.subscribe( {
            next: () => {},
            complete: () => {
              // console.log(`peer ${this.id} finish collect local answer and ice candidates`)
              if (!this.peerConnection) { return }
              subscriber.next([this.parseSDP(this.peerConnection.localDescription),
                this.parseIceCandidate(this.iceCandidateArray)])
            },
            error: () => {},
          })
        }).catch((e) => {
          console.error(`peer ${this.id} set local description fail, error is: ${e.message}`)
          subscriber.error(e)
        })
      }).catch((e) => {
        console.error(`peer ${this.id} set remote description fail, error is: ${e.message}`)
        subscriber.error(e)
      })
    })
    return sourceCreateAnswer
  }

  public receiveAnswer(desc: string, iceCandidates: string[]): Observable<boolean> {
    return new Observable<boolean>( (subscriber) => {
      this.peerConnection.setRemoteDescription(this.toSessionSDP(desc, "answer"))
          .then( () => {
            // console.log(`peer ${this.id} set remote description success`)
            this.toIceCandidate(iceCandidates).forEach((ice, index) => {
              this.peerConnection.addIceCandidate(ice)
                  // .then(() => console.log(`peer ${this.id} add ice success ${ice.candidate}`))
                  .catch((e) => {
                    // console.warn(`peer ${this.id} add remote ice fail, error is ${e.message}`)
                    // console.warn(`peer ${this.id} add remote ice fail, error is ${e.name}`)
                    // console.warn(`${index}: peer signaling state is ${this.peerConnection.signalingState}`)
                    // console.warn(`${index}: peer connection state is ${this.peerConnection.iceConnectionState}`)
                    // console.warn(`${index}: ice value is: ${ice.candidate} ${ice.sdpMLineIndex} ${ice.sdpMid}`)
                  })
            })
            subscriber.next(true)
          })
          .catch((e) => {
            subscriber.next(false)
            console.error(`peer ${this.id} set remote description error: ${e.message}`)
          })
    })
  }

  public notifyStateChanges(observer?: PartialObserver<RTCIceConnectionState>): Subscription {
    return this.peerStateChangesSubject.subscribe(observer)
  }

  public next = (msg: peers.Message): Observable<IPeerErrors> => {
    return new Observable<IPeerErrors>((subscriber) => {
      const errorVerify: string = peers.Message.verify(msg)
      if (errorVerify != null) {
        // console.log(this.id + `Trying to send an invalid message: ${errorVerify}`)
        subscriber.error(errorVerify)
      }
      try {
        this.syncChannel.send(peers.Message.encode(msg).finish())
        // console.log(`peer ${this.id} sent data`)
        subscriber.next({Type: PeerErrorsType.NoError})
      } catch (e) {
        subscriber.error({Type: PeerErrorsType.SendError, Args: e})
      }
      subscriber.complete()
    })
  }

  public subscribe(observer?: PartialObserver<peers.Message>): Subscription {
    return this.peerChannelSubject.subscribe(observer)
  }

  public getState(): RTCIceConnectionState {
    return this.pcCurrentState
  }

  private onIceCandidateHandle = (event: RTCPeerConnectionIceEvent): void => {
    if ((typeof event !== "object") || (event.candidate === undefined)) {
      return
    }
    if (event.candidate !== null) {
      this.iceCandidateArray.push(event.candidate)
    }
  }

  private onIceConnectionStateChangeHandle = (event: Event): void => {
    // console.log(`peer ${this.id} new ice connection state: ${this.peerConnection.iceConnectionState}`)
    const pcTarget: any = event.target
    switch (pcTarget.iceConnectionState) {
      case "new":
      case "checking":
        this.pcCurrentState = "new"
        this.peerStateChangesSubject.next("new")
        break
      case "disconnected":
      case "failed":
      case "closed":
        this.pcCurrentState = "disconnected"
        this.peerStateChangesSubject.next("disconnected")
        break
      case "completed":
      case "connected":
      default:
    }
  }

  private onIceGatheringStateChangeHandle = (event: Event): void => {
    // console.log(`peer ${this.id} new ice gathering state: ${this.peerConnection.iceGatheringState}`)
    const pcTarget: any = event.target
    switch (pcTarget.iceGatheringState) {
      case "gathering":
        this.iceGatherTimer = setTimeout(
            () => {
              if (this.iceCandidateArray.length) {
                this.iceGatherSubject.next(this.parseIceCandidate(this.iceCandidateArray))
                this.iceGatherSubject.complete()
                return
              }
              console.warn(`peer ${this.id} gather ice candidate timeout`)
              this.iceGatherSubject.complete()
              if (this.iceGatherTimer) {
                clearTimeout(this.iceGatherTimer)
                this.iceGatherTimer = null
              }
            },
            this.iceGatherTimeout,
        )
        break
      case "complete":
        if (this.iceCandidateArray.length > 0) {
          this.iceGatherSubject.next(this.parseIceCandidate(this.iceCandidateArray))
          this.iceGatherSubject.complete()
        }
        if (this.iceGatherTimer) {
          clearTimeout(this.iceGatherTimer)
          this.iceGatherTimer = null
        }
        break
      default:
    }
  }

  private bindDataChannelHandler = (channel: RTCDataChannel): void => {
    channel.binaryType = "arraybuffer"
    channel.onmessage = this.handleIncomingMessage
    channel.onopen = () => this.handleDataChannelChange("open")
    channel.onclose = () => this.handleDataChannelChange("closed")
  }

  private onDataChannel = (event: RTCDataChannelEvent): void => {
    if (event.channel) {
      this.syncChannel = event.channel
      this.bindDataChannelHandler(this.syncChannel)
    }
  }

  private handleIncomingMessage = (ev: MessageEvent): void => {
    if (ev.data) {
      let incoming: peers.Message
      try {
        incoming = peers.Message.decode(new Uint8Array(ev.data))
      } catch (err) {
        console.warn(`Invalid incoming message from ${this.id}: ${err}`)
        return
      }
      this.peerChannelSubject.next(incoming)
      return
    }
    console.warn(`peer ${this.id} coming msg has no data`)
  }

  private handleDataChannelChange = (state: RTCDataChannelState): any => {
    switch (state) {
      case "open":
        this.pcCurrentState = "connected"
        this.peerStateChangesSubject.next("connected")
        break
      case "closed":
        this.pcCurrentState = "disconnected"
        this.peerStateChangesSubject.next("disconnected")
      default:
    }
  }

  private toIceCandidate = (iceArray: string[]): RTCIceCandidate[] => {
    return iceArray.map( (ele: string) => new RTCIceCandidate({candidate: ele, sdpMLineIndex: 0}))
  }

  private parseIceCandidate = (iceCandidates: RTCIceCandidate[]): string[] => {
    return iceCandidates.filter((ice: RTCIceCandidate): boolean => ice !== null)
        .map( (ice) => ice.candidate)
  }

  private toSessionSDP = (decr: string, typeDecr: RTCSdpType): RTCSessionDescription => {
    return new RTCSessionDescription({ sdp: decr, type: typeDecr})
  }

  private parseSDP = (sessionDesc: RTCSessionDescription | RTCSessionDescriptionInit): string => {
    return sessionDesc.sdp
  }
}
export { IEBPeerConnector, gatherIceCandidates, createPeerConnector, PeerErrorsType, IPeerErrors }
